import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  Chip,
  Input,
  ListBox,
  Select,
  Spinner,
  Switch,
  TextArea,
  TextField,
} from '@heroui/react';
import { ArrowLeft } from 'lucide-react';
import { useLocationPage } from './useLocationPage';
import { FieldLabel } from '@/components/molecules/FieldLabel';
import { PageContainer } from '@/components/templates/PageContainer';

/** Заголовок карточки по шкале типографики HeroUI: h5 — «Card titles» (18px/600). */
function CardHeading({ children }: { children: React.ReactNode }) {
  return <Card.Title className="text-lg font-semibold">{children}</Card.Title>;
}

export function LocationPage() {
  const {
    isStaff,
    locationId,
    location,
    clients,
    form,
    isLoading,
    notFound,
    isSaving,
    isToggling,
    setField,
    handleSave,
    handleToggleEnabled,
  } = useLocationPage();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted">
        <Spinner size="sm" />
        Загрузка…
      </div>
    );
  }

  if (notFound) {
    return <p className="py-20 text-center text-sm text-muted">Локация не найдена</p>;
  }

  return (
    <PageContainer className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/locations"
            className="flex shrink-0 items-center gap-1 text-sm text-muted hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            Локации
          </Link>
          <h2 className="truncate text-2xl font-bold text-foreground">
            {locationId ? (location?.name ?? 'Локация') : 'Новая локация'}
          </h2>
          {location && (
            <Chip color={location.enabled ? 'success' : 'danger'} size="sm" variant="soft">
              {location.enabled ? 'Активна' : 'Неактивна'}
            </Chip>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button onPress={() => void handleSave()} isDisabled={isSaving}>
            {isSaving ? <Spinner size="sm" /> : 'Сохранить'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_320px]">
        <div className="flex min-w-0 flex-col gap-4">
          <Card className="bg-surface">
            <Card.Header className="flex-row items-center justify-between">
              <CardHeading>Основное</CardHeading>
              {location && (
                <Switch
                  isSelected={location.enabled}
                  isDisabled={isToggling}
                  onChange={(selected) => void handleToggleEnabled(selected)}
                >
                  <Switch.Content>
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    Активна
                  </Switch.Content>
                </Switch>
              )}
            </Card.Header>
            <Card.Content className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextField
                  variant="secondary"
                  value={form.internal_id}
                  onChange={(v) => setField('internal_id', v)}
                  isRequired
                >
                  <FieldLabel>Внутренний ID</FieldLabel>
                  <Input placeholder="store-001" />
                </TextField>

                <TextField
                  variant="secondary"
                  value={form.internal_id_2}
                  onChange={(v) => setField('internal_id_2', v)}
                >
                  <FieldLabel>Внутренний ID 2</FieldLabel>
                  <Input />
                </TextField>

                <TextField
                  variant="secondary"
                  value={form.name}
                  onChange={(v) => setField('name', v)}
                  isRequired
                >
                  <FieldLabel>Название</FieldLabel>
                  <Input placeholder="Магазин «Пример»" />
                </TextField>

                <TextField
                  variant="secondary"
                  value={form.type}
                  onChange={(v) => setField('type', v)}
                >
                  <FieldLabel>Тип</FieldLabel>
                  <Input placeholder="Магазин" />
                </TextField>
              </div>

              {isStaff && (
                <Select
                  className="w-full"
                  variant="secondary"
                  value={form.client_id}
                  onChange={(key) => setField('client_id', String(key ?? ''))}
                  placeholder="Выберите клиента"
                >
                  <FieldLabel>Клиент</FieldLabel>
                  <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover>
                    <ListBox>
                      {clients.map((client) => (
                        <ListBox.Item key={client.id} id={client.id} textValue={client.title}>
                          {client.title}
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </Select.Popover>
                </Select>
              )}
            </Card.Content>
          </Card>

          <Card className="bg-surface">
            <Card.Header>
              <CardHeading>Адрес</CardHeading>
            </Card.Header>
            <Card.Content className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextField
                  variant="secondary"
                  value={form.city}
                  onChange={(v) => setField('city', v)}
                >
                  <FieldLabel>Город</FieldLabel>
                  <Input />
                </TextField>
                <TextField
                  variant="secondary"
                  value={form.region}
                  onChange={(v) => setField('region', v)}
                >
                  <FieldLabel>Округ</FieldLabel>
                  <Input />
                </TextField>
                <TextField
                  variant="secondary"
                  value={form.province}
                  onChange={(v) => setField('province', v)}
                >
                  <FieldLabel>Область</FieldLabel>
                  <Input />
                </TextField>
                <TextField
                  variant="secondary"
                  value={form.district}
                  onChange={(v) => setField('district', v)}
                >
                  <FieldLabel>Район</FieldLabel>
                  <Input />
                </TextField>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-foreground">Адрес</span>
                <TextArea
                  variant="secondary"
                  aria-label="Адрес"
                  className="min-h-16"
                  value={form.address}
                  onChange={(e) => setField('address', e.target.value)}
                  placeholder="Полный адрес локации"
                />
              </div>
            </Card.Content>
          </Card>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <Card className="bg-surface">
            <Card.Header>
              <CardHeading>Контакты и описание</CardHeading>
            </Card.Header>
            <Card.Content className="flex flex-col gap-4">
              <TextField
                variant="secondary"
                value={form.phone}
                onChange={(v) => setField('phone', v)}
              >
                <FieldLabel>Телефон</FieldLabel>
                <Input placeholder="+7 900 000-00-00" />
              </TextField>
              <TextField variant="secondary" value={form.url} onChange={(v) => setField('url', v)}>
                <FieldLabel>Сайт</FieldLabel>
                <Input placeholder="https://example.com" />
              </TextField>
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-foreground">Описание</span>
                <TextArea
                  variant="secondary"
                  aria-label="Описание"
                  className="min-h-24"
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                />
              </div>
            </Card.Content>
          </Card>

          <Card className="bg-surface">
            <Card.Header>
              <CardHeading>Координаты</CardHeading>
            </Card.Header>
            <Card.Content className="flex flex-col gap-4">
              <TextField
                variant="secondary"
                value={form.latitude}
                onChange={(v) => setField('latitude', v)}
              >
                <FieldLabel>Широта</FieldLabel>
                <Input placeholder="55.751244" />
              </TextField>
              <TextField
                variant="secondary"
                value={form.longitude}
                onChange={(v) => setField('longitude', v)}
              >
                <FieldLabel>Долгота</FieldLabel>
                <Input placeholder="37.618423" />
              </TextField>
            </Card.Content>
          </Card>
        </div>
      </div>
    </PageContainer>
  );
}
